import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Link } from "react-router-dom";
import { ScheduleConsultationDialog } from "@/components/ScheduleConsultationDialog";
import backgroundNoise from "@/assets/background-noise.svg";
import { RelatedBlogPosts } from "@/components/RelatedBlogPosts";

const ServiceMobileApp = () => {
  const heroReveal = useScrollReveal();
  const contentReveal = useScrollReveal();

  const offerings = [
    {
      title: "Custom App Design & Development",
      description: "Intuitive, scalable apps tailored to your goals"
    },
    {
      title: "Marketing Strategy Integration",
      description: "Apps as core parts of your marketing funnel"
    },
    {
      title: "Marketing Automation & Tool Integration",
      description: "Real-time, personalized engagement"
    },
    {
      title: "Analytics & Real-Time Performance Tracking",
      description: "Live dashboards and predictive insights"
    },
    {
      title: "Load Speed & Mobile Optimization",
      description: "Lightning-fast, responsive experiences"
    },
    {
      title: "App Launch Support",
      description: "Beta testing, ASO, and cross-channel promotion"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-[50vh] md:min-h-[60vh] flex items-center justify-center px-4 sm:px-6 md:px-8 pt-20 sm:pt-24 md:pt-32 pb-10 sm:pb-12 md:pb-20 bg-foreground text-background overflow-hidden"
        style={{
          backgroundImage: `url(${backgroundNoise})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div 
          ref={heroReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            heroReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-accent text-xs sm:text-sm font-semibold mb-3 sm:mb-4 uppercase tracking-wider">
              Mobile App Development
            </div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight px-2">
              Intelligent Mobile <span className="text-accent italic">Experiences</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-background/80 leading-relaxed max-w-3xl mx-auto px-2">
              We help businesses strengthen customer loyalty with apps that create direct, personalized engagement, turning one-time users into long-term brand advocates.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-background">
        <div 
          ref={contentReveal.ref}
          className={`container mx-auto transition-all duration-1000 ${
            contentReveal.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="max-w-4xl mx-auto">
            {/* Overview */}
            <div className="mb-12 sm:mb-16 md:mb-20">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">Mobile Apps That Drive Loyalty & Revenue</h2>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-3 sm:mb-4">
                In a mobile-first world, having a dedicated app isn't just a nice-to-have—it's a direct channel to your customers' pockets and attention. We create mobile experiences that delight users, drive engagement, and become integral to your marketing strategy.
              </p>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6 sm:mb-8">
                From concept to launch and beyond, we build apps that combine beautiful design with powerful functionality and seamless integration into your broader digital ecosystem.
              </p>
              
              {/* Inline CTA */}
              <div className="bg-accent/10 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 border-2 border-accent/20">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-6">
                  <div className="flex-1">
                    <h3 className="text-lg sm:text-xl font-bold mb-2">Turn Your App Idea Into Reality</h3>
                    <p className="text-muted-foreground text-sm sm:text-base">Get expert guidance from concept to app store launch.</p>
                  </div>
                  <ScheduleConsultationDialog
                    buttonText="Discuss Your App"
                    buttonClassName="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
                  >
                    <button className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl whitespace-nowrap">
                      Discuss Your App
                    </button>
                  </ScheduleConsultationDialog>
                </div>
              </div>
            </div>

            {/* Key Offerings */}
            <div className="mb-12 sm:mb-16 md:mb-20">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">What We Offer</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                {offerings.map((offering, index) => (
                  <div key={index} className="flex items-start gap-3 sm:gap-4 p-4 sm:p-6 bg-muted/30 rounded-xl sm:rounded-2xl">
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-accent/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-accent text-xs sm:text-sm font-bold">✓</span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2">{offering.title}</h3>
                      <p className="text-muted-foreground text-xs sm:text-sm">{offering.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-12 sm:mb-16 md:mb-20 p-6 sm:p-8 md:p-10 lg:p-12 bg-foreground text-background rounded-2xl sm:rounded-3xl">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8">Why Choose Our Mobile App Services?</h2>
              <div className="space-y-5 sm:space-y-6 mb-6 sm:mb-8">
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-accent">Marketing-First Approach</h3>
                  <p className="text-background/80 text-sm sm:text-base">Apps designed as powerful marketing tools, not just standalone products.</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-accent">User-Centric Design</h3>
                  <p className="text-background/80 text-sm sm:text-base">Intuitive interfaces that users love, driving retention and engagement.</p>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-accent">Scalable Architecture</h3>
                  <p className="text-background/80 text-sm sm:text-base">Built to grow with your business from launch to millions of users.</p>
                </div>
              </div>
              
              {/* CTA inside benefits box */}
              <div className="pt-5 sm:pt-6 border-t border-background/20">
                <p className="text-background/90 text-base sm:text-lg mb-3 sm:mb-4 font-semibold">
                  Ready to launch an app your customers will love?
                </p>
                <ScheduleConsultationDialog
                  buttonText="Start Building Your App"
                  buttonClassName="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all"
                >
                  <button className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-semibold hover:bg-accent/90 transition-all">
                    Start Building Your App
                  </button>
                </ScheduleConsultationDialog>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Blog Posts */}
      <RelatedBlogPosts 
        category="Mobile App Development"
        description="Explore our latest thinking on mobile app strategy"
      />

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-background">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6 leading-tight px-2">
              Ready to Build Your <span className="text-accent italic">Mobile Experience?</span>
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-6 sm:mb-8 leading-relaxed px-2">
              Let's discuss how a mobile app can transform customer engagement and drive business growth.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
              <ScheduleConsultationDialog
                buttonText="Schedule a Consultation"
                buttonClassName="px-8 py-4 bg-foreground text-background rounded-full text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105"
              >
                <button className="px-8 py-4 bg-foreground text-background rounded-full text-lg font-semibold hover:bg-foreground/90 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                  Schedule a Consultation
                </button>
              </ScheduleConsultationDialog>
              <Link to="/services">
                <button className="px-8 py-4 border-2 border-foreground/20 hover:bg-foreground/5 rounded-full text-lg font-semibold transition-all">
                  View All Services
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceMobileApp;
